// React and MobX
import React, { useState } from "react";
import { useObserver } from "mobx-react-lite";

// Material-UI Core and Styles
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Box, Grid, Typography, Tab } from "@material-ui/core";

// Core
import { useRouter } from "Core/Utils";
import { Loader } from "Core/Components/Loader";
import { HoverScrollableGallery } from "Core/Components/HoverScrollableGallery";

// Custom Components
import { PrivateBlob } from "Custom/Components/Image/PrivateImage";
import { Error } from "Custom/Components/Error";

// Custom Styles
import { DashboardPageStyles, MainPageStyles, usePageAnimationStyles } from "Custom/StylesAppSpecific/DashboardPagesStyling";
import { PropertyDetailsStyles, TabsCustom } from "Custom/StylesAppSpecific/DashboardSubViews/PropertyDetailsPageStyling";

// Custom ViewModels
import { PropertyViewModel } from "Custom/ViewModels/DashboardSubViewModels/PropertyViewModel";

// Enums and Models
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";

// Custom Content
import { ReactComponent as Bed } from "../../Content/bed.svg";
import { ReactComponent as Shower } from "../../Content/shower.svg";
import { ReactComponent as Parking } from "../../Content/Parking.svg";
import { DashboardViewModel } from "Custom/ViewModels/DashboardViewModel";

export const PropertyDetails: React.FC = () => {
    const router = useRouter();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md")) ? true : false;

    const propertyId = (router.match.params as any).propertyId;

    const pageAnimationStyles = usePageAnimationStyles();
    const dashboardPageStyles = DashboardPageStyles();
    const propertyDetailsStyles = PropertyDetailsStyles();
    const mainPageStyles = MainPageStyles();

    // Use the shared viewModel
    const [dashboardViewModel] = useState(() => new DashboardViewModel(propertyId, false));
    const [viewModelProperty] = useState(() => new PropertyViewModel(propertyId));

    // State for tab navigation
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [tabValue, setTabValue] = useState(0);

    const [isDropdownOpen, setDropdownOpen] = useState(false);

    const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget.parentElement!.parentElement!);
        setDropdownOpen(!isDropdownOpen);
    };

    const mouseDown = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.stopPropagation();
    };

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setTabValue(newValue);
    };

    const renderBusy = () => <Loader loadingText="Fetching property details..." />;
    const renderError = () => <Error errorText="Failed to retrieve property information." />;

    //property images
    const renderPropertyImages = () => {
        const houseTypeImageUrl = dashboardViewModel.dashboardDetail.houseTypeImageUrl;
        const houseTypeThumbnailUrl = dashboardViewModel.dashboardDetail.houseTypeThumbnailUrl;

        const houseTypeImage = houseTypeImageUrl && houseTypeImageUrl.split(".").pop() !== "pdf" ? houseTypeImageUrl : houseTypeThumbnailUrl;
        const galleryImages = viewModelProperty.getPropertyHouseTypeGeneralImages || [];
        const hasGalleryImages = galleryImages.length > 0;

        return (
            <Grid item xs={12} md={6}>
                <Box style={{ height: "100%", overflow: "unset" }}>
                    <PrivateBlob
                        className={mainPageStyles.imageCard}
                        src={houseTypeImage}
                        title={"subTitle"}
                        cardMedia={true}
                        style={{ width: "100%", objectFit: "cover", backgroundColor: "#fff", height: isDesktop ? "calc(100% - 130px)" : "" }}
                    />
                    {hasGalleryImages && <HoverScrollableGallery images={galleryImages} />}
                </Box>
            </Grid>
        );
    };

    //development image
    const renderDevelopmentImage = () => {
        const developmentImageUrl = viewModelProperty.Property.developmentImageURL;
        const developmentThumbnailImageUrl = viewModelProperty.Property.developmentThumbnailImageUrl;

        const developmentImage = developmentImageUrl && developmentImageUrl.split(".").pop() !== "pdf" ? developmentImageUrl : developmentThumbnailImageUrl;

        return (
            <Grid item xs={12} md={6}>
                <Box className="propertyDevelopmentMainImage">
                    <PrivateBlob
                        className={`${mainPageStyles.imageCard}`}
                        src={developmentImage}
                        title={"subTitle"}
                        cardMedia={true}
                        style={{ width: "100%", objectFit: "cover", backgroundSize: "cover", backgroundColor: "#fff" }}
                    />
                </Box>
            </Grid>
        );
    };

    //floorplan images
    const renderFloorplans = () => {
        const floorPlanImages = viewModelProperty.getPropertyHouseTypeFloorplanImages || [];
        const hasFloorPlanImages = floorPlanImages.length > 0;
        const floorPlanImage = floorPlanImages.filter(f => f.imageUrl)[0];

        return (
            <>
                {floorPlanImage && floorPlanImage.imageUrl && (
                    <>
                        <Typography variant="h6" className={propertyDetailsStyles.floorplanTitle}>
                            Floorplans
                        </Typography>
                        <Box className={propertyDetailsStyles.floorplanImgBlock}>
                            <Box>
                                <PrivateBlob
                                    className={propertyDetailsStyles.imageCard}
                                    src={floorPlanImage && floorPlanImage.imageUrl.split(".").pop() !== "pdf" ? floorPlanImage.imageUrl : floorPlanImage.thumbnailImageUrl}
                                    title={"subTitle"}
                                    cardMedia={true}
                                    style={{ width: "100%", objectFit: "cover", backgroundColor: "#fff" }}
                                />
                            </Box>

                            {hasFloorPlanImages && <HoverScrollableGallery images={floorPlanImages} />}
                        </Box>
                    </>
                )}
            </>
        );
    };

    //Site maps images
    const renderSitemaps = () => {
        const sitePlanImages = viewModelProperty.getDevelopmentSiteMapImages || [];
        const hasSitePlanImages = sitePlanImages.length > 0;
        const sitePlanImage = sitePlanImages.filter(f => f.imageUrl)[0];

        return (
            <Box mt={4}>
                {sitePlanImage && sitePlanImage.imageUrl && (
                    <>
                        <Typography variant="h6" className={propertyDetailsStyles.floorplanTitle}>
                            Site maps
                        </Typography>
                        <Box className={propertyDetailsStyles.floorplanImgBlock}>
                            <Box>
                                <PrivateBlob
                                    className={propertyDetailsStyles.imageCard}
                                    src={sitePlanImage && sitePlanImage.imageUrl.split(".").pop() !== "pdf" ? sitePlanImage.imageUrl : sitePlanImage.thumbnailImageUrl}
                                    title={"subTitle"}
                                    cardMedia={true}
                                    style={{ width: "100%", objectFit: "cover", backgroundColor: "#fff" }}
                                />
                            </Box>
                            {hasSitePlanImages && <HoverScrollableGallery images={sitePlanImages} />}
                        </Box>
                    </>
                )}
            </Box>
        );
    };

    // render plot details
    const renderPlotDetails = () => {
        const floorPlanImages = viewModelProperty.getPropertyHouseTypeFloorplanImages || [];
        const hasFloorPlans = floorPlanImages.length > 0;

        const houseTypeAreas = dashboardViewModel.dashboardDetail.houseTypeAreas || [];
        const hasHouseTypeAreas = houseTypeAreas.length > 0;

        return (
            <Grid item xs={12} md={6}>
                <Box className={propertyDetailsStyles.propertyDetails} style={{ height: "unset", overflow: "unset" }}>
                    <Box className={mainPageStyles.detailsSection} style={{ flex: "1" }}>
                        <Box className={mainPageStyles.contentTop}>
                            <Box className={mainPageStyles.propertyTitleRow} onClick={e => handleClick(e)} onMouseDown={e => mouseDown(e)}>
                                <Box className={dashboardPageStyles.titleParent}>
                                    <Typography className={mainPageStyles.title}>{dashboardViewModel.PropertySummary}</Typography>
                                </Box>
                                <Typography className={mainPageStyles.propertySubTitle}>{dashboardViewModel.dashboardDetail.propertyAddress}</Typography>
                            </Box>
                        </Box>

                        <Box className={mainPageStyles.contentBottom}>
                            <Typography className={mainPageStyles.contentBottomText}>{dashboardViewModel.dashboardDetail.houseTypeName}</Typography>
                            <Box className={mainPageStyles.contentBottomList}>
                                <Box className={mainPageStyles.contentBottomItem}>
                                    <Bed /> {dashboardViewModel.dashboardDetail.numberOfBeds}
                                </Box>
                                <Box className={mainPageStyles.contentBottomItem}>
                                    <Shower />
                                    {dashboardViewModel.dashboardDetail.numberOfBathrooms}
                                </Box>
                                <Box className={mainPageStyles.contentBottomItem}>
                                    <Parking /> {dashboardViewModel.dashboardDetail.parkingSpaceCount}
                                </Box>
                            </Box>
                        </Box>
                        <Box className={mainPageStyles.contentDescription} dangerouslySetInnerHTML={{ __html: viewModelProperty.Description }} />
                    </Box>

                    {hasHouseTypeAreas && (
                        <Box className={propertyDetailsStyles.dimensionsList}>
                            <Typography variant="h6" color="textSecondary" className={propertyDetailsStyles.dimensionsListTitle}>
                                Room Dimensions
                            </Typography>
                            <Box className={propertyDetailsStyles.dimensionsListBlock}>
                                {houseTypeAreas.map((room, index) => (
                                    <Box key={index} className={propertyDetailsStyles.dimensionsListItem}>
                                        <Typography variant="body2">
                                            <strong>{room.areaName}</strong> {room.sqft}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {hasFloorPlans && <Box className={propertyDetailsStyles.floorplanImage}>{renderFloorplans()}</Box>}
                </Box>
            </Grid>
        );
    };

    const renderPlotDetailsForMobile = () => {
        const floorPlanImages = viewModelProperty.getPropertyHouseTypeFloorplanImages || [];
        const hasFloorPlans = floorPlanImages.length > 0;

        const houseTypeAreas = dashboardViewModel.houseTypeAreaOptions || [];
        const hasHouseTypeAreas = houseTypeAreas.length > 0;

        return (
            <Grid style={{ height: "100%" }} item xs={12} md={6}>
                <Box className={propertyDetailsStyles.propertyDetails}>
                    <Box className={mainPageStyles.detailsSection} style={{ flex: "1", height: "100%" }}>
                        <Box className={mainPageStyles.contentTop}>
                            <Box className={mainPageStyles.propertyTitleRow} onClick={e => handleClick(e)} onMouseDown={e => mouseDown(e)}>
                                <Box className={dashboardPageStyles.titleParent}>
                                    <Typography className={mainPageStyles.title}>{dashboardViewModel.PropertySummary}</Typography>
                                </Box>
                                <Typography className={mainPageStyles.propertySubTitle}>{dashboardViewModel.dashboardDetail.propertyAddress}</Typography>
                            </Box>
                        </Box>
                        <Box className={mainPageStyles.contentBottom}>
                            <Typography className={mainPageStyles.contentBottomText}>{dashboardViewModel.dashboardDetail.houseTypeName}</Typography>
                            <Box className={mainPageStyles.contentBottomList}>
                                <Box className={mainPageStyles.contentBottomItem}>
                                    <Bed /> {dashboardViewModel.dashboardDetail.numberOfBeds}
                                </Box>
                                <Box className={mainPageStyles.contentBottomItem}>
                                    <Shower />
                                    {dashboardViewModel.dashboardDetail.numberOfBathrooms}
                                </Box>
                                <Box className={mainPageStyles.contentBottomItem}>
                                    <Parking /> {dashboardViewModel.dashboardDetail.parkingSpaceCount}
                                </Box>
                            </Box>
                        </Box>

                        {/* property image */}
                        {renderPropertyImages()}

                        <Box className={mainPageStyles.contentDescription} dangerouslySetInnerHTML={{ __html: viewModelProperty.Description }} />
                    </Box>

                    {hasHouseTypeAreas && (
                        <Box className={propertyDetailsStyles.dimensionsList}>
                            <Typography variant="h6" color="textSecondary" className={propertyDetailsStyles.dimensionsListTitle}>
                                Room Dimensions
                            </Typography>
                            <Box className={propertyDetailsStyles.dimensionsListBlock}>
                                {houseTypeAreas.map((room, index) => (
                                    <Box key={index} className={propertyDetailsStyles.dimensionsListItem}>
                                        <Typography variant="body2">
                                            <strong>{room.areaName}</strong> {room.sqft}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {hasFloorPlans && <Box className={propertyDetailsStyles.floorplanImage}>{renderFloorplans()}</Box>}
                </Box>
            </Grid>
        );
    };

    //render development details
    const renderDevelopmentDetails = () => {
        const siteMapImages = viewModelProperty.getDevelopmentSiteMapImages || [];
        const hasSitemaps = siteMapImages.length > 0;

        return (
            <Grid item xs={12} md={6}>
                <Box className={propertyDetailsStyles.propertyDetails} style={{ display: "flex", flexDirection: hasSitemaps ? "column" : "row" }}>
                    <Box style={{ height: "100%" }}>
                        <Box className={mainPageStyles.detailsSection} style={{ flex: 1 }}>
                            <Box className={mainPageStyles.contentTop}>
                                <Box className={mainPageStyles.propertyTitleRow} onClick={handleClick} onMouseDown={mouseDown}>
                                    <Box className={dashboardPageStyles.titleParent}>
                                        <Typography className={mainPageStyles.title}>{dashboardViewModel.dashboardDetail.developmentName}</Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Box className={mainPageStyles.contentBottom}></Box>

                            <Box
                                className={mainPageStyles.contentDescription}
                                dangerouslySetInnerHTML={{
                                    __html: viewModelProperty.Property.developmentDescription,
                                }}
                            />
                        </Box>

                        {hasSitemaps && <Box className={propertyDetailsStyles.floorplanImage}>{renderSitemaps()}</Box>}
                    </Box>
                </Box>
            </Grid>
        );
    };

    const renderDevelopmentDetailsForMobile = () => {
        const siteMapImages = viewModelProperty.getDevelopmentSiteMapImages || [];
        const hasSitemaps = siteMapImages.length > 0;

        return (
            <Grid item xs={12} md={6}>
                <Box className={propertyDetailsStyles.propertyDetails}>
                    <Box className={mainPageStyles.detailsSection} style={{ flex: "1" }}>
                        <Box className={mainPageStyles.contentTop}>
                            <Box className={mainPageStyles.propertyTitleRow} onClick={e => handleClick(e)} onMouseDown={e => mouseDown(e)}>
                                <Box className={dashboardPageStyles.titleParent}>
                                    <Typography className={mainPageStyles.title}>{dashboardViewModel.dashboardDetail.developmentName}</Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box>{renderDevelopmentImage()}</Box>
                        <Box className={mainPageStyles.contentDescription} dangerouslySetInnerHTML={{ __html: viewModelProperty.Property.developmentDescription }} />
                    </Box>

                    {/*development site maps */}
                    {hasSitemaps && <Box className={propertyDetailsStyles.floorplanImage}>{renderSitemaps()}</Box>}
                </Box>
            </Grid>
        );
    };

    const renderItem = () => (
        <Grid
            container
            spacing={0}
            className={`${propertyDetailsStyles.detailCard} ${tabValue === 0 ? propertyDetailsStyles.detailCardPlot : propertyDetailsStyles.detailCardDev}`}
        >
            <>
                {isDesktop ? (
                    <>
                        {tabValue === 0 ? renderPropertyImages() : renderDevelopmentImage()}
                        {tabValue === 0 ? renderPlotDetails() : renderDevelopmentDetails()}
                    </>
                ) : (
                    <> {tabValue === 0 ? renderPlotDetailsForMobile() : renderDevelopmentDetailsForMobile()}</>
                )}
            </>
        </Grid>
    );

    return useObserver(() => (
        <Box className={`${dashboardPageStyles.root} ${mainPageStyles.root} ${pageAnimationStyles.pageWrapper} propertyDetialsWrapper`}>
            <Box className={`${dashboardPageStyles.workspace} ${mainPageStyles.workspace} propertyDetailsWorkspace`}>
                <TabsCustom value={tabValue} onChange={handleTabChange} aria-label="property-tabs" className={propertyDetailsStyles.tabsWidth}>
                    <Tab label="Your Plot" />
                    <Tab label="The Development" />
                </TabsCustom>
                {viewModelProperty.ApiStatus === ApiStatus.success && renderItem()}
                {viewModelProperty.ApiStatus === ApiStatus.loading && renderBusy()}
                {viewModelProperty.ApiStatus === ApiStatus.error && renderError()}
            </Box>
        </Box>
    ));
};
